import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@swiftlink.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (!["admin", "viewer", "operator"].includes(u.role)) {
        toast.error("Ce compte n'est pas un administrateur");
        return;
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--sl-primary-dark)" }}>
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-full grid place-items-center bg-[var(--sl-secondary)] text-gray-900">
            <Send size={18} />
          </span>
          <span className="font-heading text-xl font-bold">Swiftlink-Africa</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Panneau<br />d'administration
          </h1>
          <p className="mt-4 text-white/70 max-w-md">
            Validez les dépôts, gérez les transferts entre le Togo, le Gabon, la France et le Rwanda, et assurez la confiance de chaque transaction.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--sl-secondary-light)]">
            <ShieldCheck size={14} /> Accès restreint
          </div>
        </div>
        <div className="text-xs text-white/40">© Swiftlink-Africa {new Date().getFullYear()}</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[var(--sl-bg)]">
        <form onSubmit={submit} className="w-full max-w-md sl-card" data-testid="admin-login-form">
          <h2 className="font-heading text-2xl font-bold">Connexion administrateur</h2>
          <p className="text-gray-600 mt-1 text-sm">Identifiants requis pour accéder au panel.</p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="sl-label">Email</label>
              <input className="sl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
            </div>
            <div>
              <label className="sl-label">Mot de passe</label>
              <div className="relative">
                <input className="sl-input pr-11" type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid="admin-login-password-toggle">
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="sl-btn-primary w-full" data-testid="admin-login-submit">
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
